import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Image as ImageIcon, ExternalLink, Loader2, Paperclip } from "lucide-react";
import api, { API } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export default function AttachmentsDialog({ open, onClose, quote, onChanged }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => { if (quote) api.get(`/quotes/${quote.id}/attachments`).then((r) => setItems(r.data)); };
  useEffect(() => { if (open) load(); }, [open, quote]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    const files = fileRef.current?.files;
    if (!files || !files.length) { toast.error("Seleziona uno o più file (PDF, JPG, PNG)"); return; }
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/quotes/${quote.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Documento firmato allegato. Preventivo segnato come Accettato.");
      if (fileRef.current) fileRef.current.value = "";
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel caricamento"); }
    finally { setUploading(false); }
  };

  const del = async (id) => { await api.delete(`/attachments/${id}`); toast.success("Allegato eliminato"); load(); };
  const openFile = (a) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/attachments/${a.id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  };

  if (!quote) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="attachments-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-primary" /> Allegati preventivo N. {quote.numero}
          </DialogTitle>
          <DialogDescription className="sr-only">Carica e gestisci gli allegati del preventivo (documento firmato).</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-dashed border-border p-4 space-y-3">
          <p className="text-sm text-muted-foreground">Carica il preventivo <strong>firmato</strong> dal cliente (PDF o foto). Allegando un file, il preventivo viene segnato come <strong>Accettato</strong>.</p>
          <Input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple data-testid="attachment-file-input" />
          <Button className="w-full" onClick={upload} disabled={uploading} data-testid="attachment-upload-button">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Carica firmato
          </Button>
        </div>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg border border-border p-2.5" data-testid={`attachment-item-${a.id}`}>
              <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0">
                {a.content_type?.includes("pdf") ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.original_filename}</div>
                <div className="text-xs text-muted-foreground">{fmtSize(a.size || 0)}</div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openFile(a)} title="Apri"><ExternalLink className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => del(a.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {items.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">Nessun allegato per questo preventivo.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
